import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Database,
  KeyRound,
  Server,
  EyeOff,
  History,
  Key,
  Trash2,
  RefreshCw,
  ClipboardCopy,
  Download,
  Upload,
  FileJson,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { getMeta, isMetaKey, removeItem as lsRemove } from '../utils/storage';
import { computeAuditEntriesHash } from '../utils/integrity';

/**
 * LocalDataInspector
 *
 * Shows a read-only audit of every `ghostcomm_*` key the app stores in the browser's
 * localStorage. Each row displays a human label, the approximate byte size, and an
 * individual "Wipe" button (with confirmation for sensitive items).
 *
 * Rationale: the panic button promises to wipe local data on demand. This panel makes
 * that promise legible — users see exactly what we store and can selectively remove it
 * without nuking their session.
 */

// Canonical registry. Keeps the UI honest when we add new keys — new storage keys
// that aren't registered will still show up under "Unknown" so nothing slips past us.
const REGISTRY = [
  {
    key: 'ghostcomm_token',
    label: 'Session token (JWT)',
    icon: Key,
    description: 'Signs you in to this server. Wiping signs you out.',
    sensitive: true,
  },
  {
    key: 'ghostcomm_keys',
    label: 'RSA key pair (JWK)',
    icon: KeyRound,
    description:
      'Your end-to-end encryption keys for this device. Wiping them means new messages sent to you will not be decryptable on this device.',
    sensitive: true,
  },
  {
    key: 'ghostcomm_server_url',
    label: 'Custom backend server URL',
    icon: Server,
    description: 'Overrides the default backend. Wiping reverts to the built-in server.',
    sensitive: false,
  },
  {
    key: 'ghostcomm_hide_undecryptable',
    label: 'Hide legacy messages toggle',
    icon: EyeOff,
    description: 'Preference only — safe to wipe.',
    sensitive: false,
  },
  {
    key: 'ghostcomm_recent_commands',
    label: 'Command palette history',
    icon: History,
    description: 'Your last used commands in the Cmd+K palette. Safe to wipe.',
    sensitive: false,
  },
];

function getItem(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function byteSize(value) {
  if (value == null) return 0;
  // A good enough approximation — localStorage stores UTF-16, so 2 bytes/char,
  // but for display we use UTF-8 byte count which matches what users expect.
  try {
    return new Blob([value]).size;
  } catch {
    return value.length;
  }
}

function prettySize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/**
 * Humanise an ISO timestamp relative to now, e.g. "4 min ago", "2 days ago".
 * Falls back to the ISO date for anything older than a year.
 */
function relativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 10) return 'just now';
  if (sec < 60) return `${sec} sec ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`;
  const month = Math.floor(day / 30);
  if (month < 12) return `${month} month${month === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

function listUnknownKeys() {
  const known = new Set(REGISTRY.map((r) => r.key));
  const unknown = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      // Skip meta siblings — they're rendered inline with their owner key.
      if (k && k.startsWith('ghostcomm_') && !known.has(k) && !isMetaKey(k)) {
        unknown.push(k);
      }
    }
  } catch {}
  return unknown;
}

export const LocalDataInspector = () => {
  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  // ─── Restore-from-audit state ──────────────────────────────────────
  // `restoreOpen` gates the Dialog; `parsed` is the successfully validated
  // audit payload (null until a good file is chosen); `parseError` holds the
  // user-facing rejection message when a file fails validation.
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState(null);
  const fileInputRef = useRef(null);

  const resetRestoreState = useCallback(() => {
    setParsed(null);
    setParseError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const wipe = (key, signOutNeeded) => {
    try {
      // Use wrapper so the paired `ghostcomm__meta_{key}` entry disappears too.
      lsRemove(key);
      toast.success('Wiped ' + key);
      refresh();
      if (signOutNeeded) {
        // Best-effort sign-out after wiping the token
        setTimeout(() => window.location.reload(), 500);
      }
    } catch (e) {
      toast.error('Wipe failed: ' + (e?.message || 'unknown'));
    }
  };

  // Copy a row's raw value + metadata as a pretty-printed JSON snapshot.
  // Useful when a user suspects tampering and wants a forensic record they
  // can paste into a secure note before wiping. For sensitive keys (token,
  // RSA private key) the caller gates this behind a confirmation dialog.
  const copyAsJson = async (key) => {
    try {
      const payload = {
        key,
        value: getItem(key),
        meta: getMeta(key),
        snapshotAt: new Date().toISOString(),
      };
      const json = JSON.stringify(payload, null, 2);
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard unavailable');
      }
      await navigator.clipboard.writeText(json);
      toast.success('Copied JSON snapshot of ' + key);
    } catch (e) {
      toast.error('Copy failed: ' + (e?.message || 'unknown'));
    }
  };

  // Bundle every present `ghostcomm_*` entry (with its metadata) into a single
  // JSON file and trigger a browser download. No clipboard hop, so this is the
  // safer path when the user wants a full forensic snapshot — the file lands
  // wherever their browser normally saves downloads.
  const downloadFullAudit = async () => {
    try {
      const entries = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (!k || !k.startsWith('ghostcomm_') || isMetaKey(k)) continue;
        entries.push({
          key: k,
          value: window.localStorage.getItem(k),
          meta: getMeta(k),
        });
      }
      // Integrity: SHA-256 over the canonical (key, value) pair list so
      // consumers can detect on-disk tampering before importing. Meta is
      // excluded from the hash because Restore auto-rewrites it.
      const integrityHash = await computeAuditEntriesHash(entries);
      const payload = {
        app: 'GhostComm',
        snapshotAt: new Date().toISOString(),
        device: {
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          language: typeof navigator !== 'undefined' ? navigator.language : null,
        },
        entryCount: entries.length,
        integrity: { algorithm: 'SHA-256', hash: integrityHash },
        entries,
      };
      const json = JSON.stringify(payload, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const stamp = new Date()
        .toISOString()
        .replace(/[:.]/g, '-')
        .replace('T', '_')
        .slice(0, 19);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ghostcomm-local-audit-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke on next tick so the browser has time to kick off the download.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded audit of ${entries.length} local entries`);
    } catch (e) {
      toast.error('Download failed: ' + (e?.message || 'unknown'));
    }
  };

  // ─── Restore: parse + validate a picked/dropped audit file ─────────
  // We are deliberately strict: we reject any entry whose key is not a
  // `ghostcomm_` primary key, so an attacker-crafted file can't inject
  // arbitrary localStorage keys (e.g. `__proto__`, unrelated app tokens).
  const handleFilePicked = (file) => {
    setParsed(null);
    setParseError(null);
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setParseError('File is too large (>5 MB). Audit files are normally a few KB.');
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => setParseError('Could not read the file.');
    reader.onload = async () => {
      try {
        const raw = reader.result;
        const data = JSON.parse(typeof raw === 'string' ? raw : '');
        if (!data || typeof data !== 'object') {
          throw new Error('Not a JSON object');
        }
        if (data.app !== 'GhostComm') {
          throw new Error('Not a Government Shield audit file (missing "app": "GhostComm")');
        }
        if (!Array.isArray(data.entries)) {
          throw new Error('Audit file has no "entries" array');
        }
        const accepted = [];
        const rejected = [];
        for (const e of data.entries) {
          if (
            e &&
            typeof e.key === 'string' &&
            e.key.startsWith('ghostcomm_') &&
            !isMetaKey(e.key) &&
            typeof e.value === 'string'
          ) {
            accepted.push(e);
          } else {
            rejected.push(e?.key ?? '(unnamed)');
          }
        }
        if (accepted.length === 0) {
          throw new Error('No valid ghostcomm_* entries found in file.');
        }
        // Integrity: v2+ audit files embed SHA-256 over the canonical entries
        // list. Verify against the ORIGINAL entries (as-downloaded, pre-filter)
        // so the hash matches byte-for-byte with what was exported.
        let integrityState = 'none'; // 'ok' | 'mismatch' | 'none'
        let integrityExpected = null;
        let integrityActual = null;
        if (data.integrity?.hash) {
          integrityExpected = data.integrity.hash;
          integrityActual = await computeAuditEntriesHash(data.entries);
          integrityState = integrityActual === integrityExpected ? 'ok' : 'mismatch';
        }
        setParsed({
          snapshotAt: data.snapshotAt || null,
          device: data.device || null,
          accepted,
          rejected,
          integrityState,
          integrityExpected,
          integrityActual,
        });
      } catch (err) {
        setParseError(err?.message || 'Could not parse file');
      }
    };
    reader.readAsText(file);
  };

  // Apply the validated audit: overwrite (or create) each key and its sibling
  // metadata. We do NOT wipe unrelated keys — the user can explicitly wipe
  // before restoring if they want a clean overlay.
  const applyRestore = () => {
    if (!parsed?.accepted?.length) return;
    if (parsed.integrityState === 'mismatch') {
      toast.error('Integrity check failed — refusing to restore.');
      return;
    }
    try {
      const META_PREFIX = 'ghostcomm__meta_';
      const now = new Date().toISOString();
      for (const e of parsed.accepted) {
        window.localStorage.setItem(e.key, e.value);
        // Restore original meta if present; otherwise stamp "restored at now".
        const metaToWrite =
          e.meta && typeof e.meta === 'object'
            ? { ...e.meta, restoredAt: now }
            : { updatedAt: now, restoredAt: now, backfilled: true };
        window.localStorage.setItem(META_PREFIX + e.key, JSON.stringify(metaToWrite));
      }
      toast.success(`Restored ${parsed.accepted.length} entries — reloading…`);
      setRestoreOpen(false);
      // Full reload so AuthContext picks up the new token + keys cleanly.
      setTimeout(() => window.location.reload(), 700);
    } catch (err) {
      toast.error('Restore failed: ' + (err?.message || 'unknown'));
    }
  };

  // Recompute on refreshTick
  useEffect(() => {}, [refreshTick]);

  // One-time backfill: for any `ghostcomm_*` key without metadata (e.g. data
  // written before this feature shipped), stamp a sentinel so the row shows
  // "Tracked since <now>" rather than blank. We intentionally do NOT invent a
  // `createdAt` — we don't know the real one — so the UI falls back to the
  // "Last touched" label honestly.
  useEffect(() => {
    try {
      const META_PREFIX = 'ghostcomm__meta_';
      const now = new Date().toISOString();
      const keys = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith('ghostcomm_') && !k.startsWith(META_PREFIX)) keys.push(k);
      }
      let wrote = false;
      keys.forEach((k) => {
        if (window.localStorage.getItem(META_PREFIX + k)) return;
        window.localStorage.setItem(
          META_PREFIX + k,
          JSON.stringify({ updatedAt: now, backfilled: true })
        );
        wrote = true;
      });
      if (wrote) setRefreshTick((t) => t + 1);
    } catch {}
  }, []);

  // Re-render every 30 s so relative timestamps ("4 min ago") stay fresh
  // while the panel is open. Cheap: only a few dozen DOM nodes refresh.
  useEffect(() => {
    const id = setInterval(() => setRefreshTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const rows = REGISTRY.map((r) => {
    const val = getItem(r.key);
    return {
      ...r,
      present: val != null,
      size: byteSize(val),
      meta: val != null ? getMeta(r.key) : null,
    };
  });

  const unknown = listUnknownKeys().map((k) => ({
    key: k,
    label: 'Unknown (' + k + ')',
    icon: Database,
    description: 'A key we did not register. Safe to wipe.',
    sensitive: false,
    present: true,
    size: byteSize(getItem(k)),
    meta: getMeta(k),
  }));

  const allRows = [...rows, ...unknown];
  const totalBytes = allRows.reduce((s, r) => s + (r.present ? r.size : 0), 0);
  const presentCount = allRows.filter((r) => r.present).length;

  return (
    <div className="p-4 rounded-lg bg-secondary/30 space-y-3" data-testid="local-data-inspector">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-300">
          <Database className="w-5 h-5" />
          <span className="font-medium">Local data on this device</span>
        </div>
        <div className="flex items-center gap-3">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="local-data-download-audit"
                aria-label="Download full audit JSON"
                disabled={presentCount === 0}
              >
                <Download className="w-3 h-3" />
                Download audit
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-card border-border">
              <AlertDialogHeader>
                <AlertDialogTitle>Download full audit JSON?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400 space-y-2">
                  <span className="block">
                    This bundles every <code className="text-slate-300 font-mono text-[11px]">ghostcomm_*</code> entry
                    on this device — including your session token and RSA private key — into a single JSON
                    file saved to your Downloads folder.
                  </span>
                  <span className="block">
                    Treat this file like a password manager export: keep it encrypted at rest, delete it when
                    you no longer need it.
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="local-data-download-audit-cancel">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={downloadFullAudit}
                  data-testid="local-data-download-audit-confirm"
                >
                  Download
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Dialog
            open={restoreOpen}
            onOpenChange={(open) => {
              setRestoreOpen(open);
              if (!open) resetRestoreState();
            }}
          >
            <DialogTrigger asChild>
              <button
                className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1"
                data-testid="local-data-restore-audit"
                aria-label="Restore from audit JSON"
              >
                <Upload className="w-3 h-3" />
                Restore
              </button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileJson className="w-5 h-5 text-primary" />
                  Restore from audit JSON
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Pick a <code className="text-slate-300 font-mono text-[11px]">ghostcomm-local-audit-*.json</code>{' '}
                  file you downloaded earlier. Its entries will overwrite any matching local data on this
                  device, then the app will reload. Only <code className="text-slate-300 font-mono text-[11px]">ghostcomm_*</code>{' '}
                  keys are accepted — anything else is rejected.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json,.json"
                  onChange={(e) => handleFilePicked(e.target.files?.[0])}
                  className="block w-full text-xs text-slate-400
                             file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0
                             file:bg-primary/20 file:text-primary file:text-xs
                             file:cursor-pointer hover:file:bg-primary/30"
                  data-testid="local-data-restore-file-input"
                />

                {parseError && (
                  <div
                    className="flex items-start gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 text-xs"
                    data-testid="local-data-restore-error"
                  >
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{parseError}</span>
                  </div>
                )}

                {parsed && (
                  <div className="space-y-2" data-testid="local-data-restore-preview">
                    <div className="flex items-start gap-2 p-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs">
                      <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p>
                          Valid audit file.{' '}
                          {parsed.snapshotAt && (
                            <span className="text-slate-400">
                              Snapshot taken {relativeTime(parsed.snapshotAt)}.
                            </span>
                          )}
                        </p>
                        <p>
                          <strong>{parsed.accepted.length}</strong> entr
                          {parsed.accepted.length === 1 ? 'y' : 'ies'} ready to restore
                          {parsed.rejected.length > 0 && (
                            <> · {parsed.rejected.length} rejected (non-ghostcomm keys)</>
                          )}
                          .
                        </p>
                      </div>
                    </div>
                    <ul className="max-h-40 overflow-auto rounded-md border border-border bg-background/40 divide-y divide-border">
                      {parsed.accepted.map((e) => (
                        <li
                          key={e.key}
                          className="flex items-center justify-between px-3 py-1.5 text-xs"
                          data-testid={`local-data-restore-row-${e.key}`}
                        >
                          <span className="font-mono text-slate-300 truncate">{e.key}</span>
                          <span className="text-slate-500 font-mono shrink-0 ml-2">
                            {prettySize(byteSize(e.value))}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {parsed.integrityState === 'ok' && (
                      <div
                        className="flex items-start gap-2 p-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs"
                        data-testid="local-data-restore-integrity-ok"
                      >
                        <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                        <div className="space-y-1">
                          <p>Integrity verified (SHA-256 match).</p>
                          <p className="text-slate-400 font-mono text-[10px] truncate">
                            {parsed.integrityActual?.slice(0, 40)}…
                          </p>
                        </div>
                      </div>
                    )}
                    {parsed.integrityState === 'mismatch' && (
                      <div
                        className="flex items-start gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 text-xs"
                        data-testid="local-data-restore-integrity-mismatch"
                      >
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <div className="space-y-1">
                          <p>
                            <strong>Integrity check FAILED.</strong> Either the file was modified
                            on disk or created by a buggy tool. Restore is blocked.
                          </p>
                          <p className="text-slate-400 font-mono text-[10px] truncate">
                            expected {parsed.integrityExpected?.slice(0, 12)}… got{' '}
                            {parsed.integrityActual?.slice(0, 12)}…
                          </p>
                        </div>
                      </div>
                    )}
                    {parsed.integrityState === 'none' && (
                      <div
                        className="flex items-start gap-2 p-3 rounded-md bg-slate-500/10 border border-slate-500/30 text-slate-400 text-[11px]"
                        data-testid="local-data-restore-integrity-none"
                      >
                        <Database className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>
                          Older audit file without an integrity hash — tampering cannot be
                          auto-detected. Newer exports include SHA-256.
                        </span>
                      </div>
                    )}
                    <p className="text-[11px] text-amber-300/80 leading-relaxed">
                      Restoring will overwrite your current session token and RSA keys. The app will reload
                      immediately after. Make sure this file came from a device you trust.
                    </p>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setRestoreOpen(false)}
                  data-testid="local-data-restore-cancel"
                >
                  Cancel
                </Button>
                <Button
                  disabled={!parsed || parsed.integrityState === 'mismatch'}
                  onClick={applyRestore}
                  className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-40"
                  data-testid="local-data-restore-confirm"
                >
                  <Upload className="w-3 h-3 mr-1" />
                  Restore &amp; reload
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <button
            onClick={refresh}
            className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1"
            data-testid="local-data-refresh"
            aria-label="Refresh"
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-500">
        {presentCount} item{presentCount === 1 ? '' : 's'} · {prettySize(totalBytes)} total. The
        emergency wipe button below removes all of these in one step.
      </p>

      <div className="space-y-1.5">
        {allRows.map((r) => {
          const Icon = r.icon;
          if (!r.present) {
            return (
              <div
                key={r.key}
                className="flex items-center gap-3 px-3 py-2 rounded-md bg-background/30 border border-border text-xs text-slate-600"
                data-testid={`local-data-row-${r.key}`}
              >
                <Icon className="w-4 h-4 shrink-0 opacity-50" />
                <span className="flex-1 truncate">{r.label}</span>
                <span className="italic">not stored</span>
              </div>
            );
          }

          const wipeBtn = (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 shrink-0"
              data-testid={`local-data-wipe-${r.key}`}
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Wipe
            </Button>
          );

          const copyBtn = (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-slate-400 hover:text-slate-200 hover:bg-secondary/60 shrink-0 px-2"
              title="Copy JSON snapshot (key + value + metadata)"
              aria-label={`Copy JSON snapshot of ${r.label}`}
              data-testid={`local-data-copy-${r.key}`}
            >
              <ClipboardCopy className="w-3 h-3" />
            </Button>
          );

          return (
            <div
              key={r.key}
              className="flex items-center gap-3 px-3 py-2 rounded-md bg-background/40 border border-border"
              data-testid={`local-data-row-${r.key}`}
            >
              <Icon className="w-4 h-4 shrink-0 text-primary" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-200 truncate">{r.label}</p>
                <p className="text-[11px] text-slate-500 truncate">{r.description}</p>
                {r.meta && (r.meta.createdAt || r.meta.updatedAt) && (
                  <p
                    className="text-[10px] text-slate-600 font-mono mt-0.5 truncate"
                    title={[
                      r.meta.createdAt && `Created: ${r.meta.createdAt}`,
                      r.meta.updatedAt && `Updated: ${r.meta.updatedAt}`,
                    ]
                      .filter(Boolean)
                      .join('\n')}
                    data-testid={`local-data-timestamps-${r.key}`}
                  >
                    {r.meta.createdAt && (
                      <>Created {relativeTime(r.meta.createdAt)}</>
                    )}
                    {r.meta.createdAt && r.meta.updatedAt && r.meta.updatedAt !== r.meta.createdAt && (
                      <> · Last touched {relativeTime(r.meta.updatedAt)}</>
                    )}
                    {!r.meta.createdAt && r.meta.updatedAt && (
                      <>
                        {r.meta.backfilled ? 'Tracked since ' : 'Last touched '}
                        {relativeTime(r.meta.updatedAt)}
                      </>
                    )}
                  </p>
                )}
              </div>
              <span className="text-xs text-slate-500 shrink-0 font-mono">{prettySize(r.size)}</span>
              {r.sensitive ? (
                <AlertDialog>
                  <AlertDialogTrigger asChild>{copyBtn}</AlertDialogTrigger>
                  <AlertDialogContent className="bg-card border-border">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Copy {r.label} to clipboard?</AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-400">
                        This is a sensitive value. Anything on your clipboard can be read by other
                        apps on this device until you copy something else. Only do this if you
                        trust this machine and are about to paste the snapshot into a secure note.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid={`local-data-copy-${r.key}-cancel`}>
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={() => copyAsJson(r.key)}
                        data-testid={`local-data-copy-${r.key}-confirm`}
                      >
                        Copy anyway
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ) : (
                React.cloneElement(copyBtn, { onClick: () => copyAsJson(r.key) })
              )}
              {r.sensitive ? (
                <AlertDialog>
                  <AlertDialogTrigger asChild>{wipeBtn}</AlertDialogTrigger>
                  <AlertDialogContent className="bg-card border-border">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Wipe {r.label}?</AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-400">
                        {r.description}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid={`local-data-wipe-${r.key}-cancel`}>
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => wipe(r.key, r.key === 'ghostcomm_token')}
                        data-testid={`local-data-wipe-${r.key}-confirm`}
                      >
                        Wipe
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ) : (
                React.cloneElement(wipeBtn, { onClick: () => wipe(r.key, false) })
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LocalDataInspector;
